import classNames from 'classnames';
import { forwardRef, useImperativeHandle } from 'react';
import { FormProvider } from 'react-hook-form';
import { useLoaderData } from 'react-router';
import { dsClass } from '../../../../common/constants/dsClasses';
import { DialogType, PhoneNumberOwnerType, PhoneNumberType, SimCardSelection } from '../../../../common/enums.js';
import { t } from '../../../../common/i18n/index.js';
import {
  getNextAllowedDay,
  getNormalizedDateFromDateString,
  isAllowedTransferDay,
} from '../../../../common/utils/dateUtils.js';
import { SimOption } from '../../../../common/utils/simCardUtils.js';
import { SimType } from '../../../../generated/api/simType.js';
import { openDialog } from '../../../../public/site/dialogStore.js';
import { NumberPrivacy } from '../../../NumberPrivacy/NumberPrivacy.js';
import { SelectPhoneNumber } from '../../../SelectPhoneNumber/SelectPhoneNumber.js';
import { isMobileM2MProduct } from '../../../ShoppingBasket/shoppingBasketHelpers.js';
import { useSubscriptionConfigForm } from '../../hooks/useSubscriptionConfigForm';
import { ACTIVATE_EXISTING, PlaceOrderSimCard } from './PlaceOrderSimCard.js';
import type { PlaceOrderLoaderData } from '../../../../common/loaders.js';
import type { BasketItem } from '../../../../common/types/shoppingBasket.js';
import type { PhoneNumberOwner, SimCardConfiguration } from '../../../../common/types/subscription.js';
import type { OnlineModel } from '../../../../generated/api/onlineModel.js';

export interface SubscriptionConfigurationHandle {
  validate: () => Promise<boolean>;
  scrollToFirstError: () => void;
  getErrorCount: () => number;
}

export interface SubscriptionConfigurationHandlerProps {
  cartItemInstanceId: string;
  onlineModel: OnlineModel;
  companyName: string;
  basketItem: BasketItem;
  mdmId: string;
  phoneNumbers: string[];
  phoneNumbersLoading: boolean;
  onLoadMoreNumbers: () => void;
  usedSimCardNumbers?: string[];
  disabledPhoneNumbers?: string[];
  /** Pre-assigned initial phone number for this subscription (to avoid duplicates when multiple subscriptions are added) */
  initialPhoneNumber?: string;
}

export const SubscriptionConfigurationHandler = forwardRef<
  SubscriptionConfigurationHandle,
  SubscriptionConfigurationHandlerProps
>(
  (
    {
      cartItemInstanceId,
      onlineModel,
      companyName,
      basketItem,
      mdmId,
      phoneNumbers,
      phoneNumbersLoading,
      onLoadMoreNumbers,
      usedSimCardNumbers = [],
      disabledPhoneNumbers = [],
      initialPhoneNumber,
    },
    ref
  ) => {
    const { holidays } = useLoaderData() as PlaceOrderLoaderData;

    const {
      innerFormMethods,
      formState,
      fieldsToShow,
      errors,
      validatedPhoneNumbers,
      validateAllFields,
      scrollToFirstError,
    } = useSubscriptionConfigForm({
      cartItemInstanceId,
      onlineModel,
      basketItem,
      mdmId,
      phoneNumbers,
    });

    const { showSelectPhoneNumber, showNumberPrivacy, showSimCard, showM2MSimCard } = fieldsToShow;

    useImperativeHandle(
      ref,
      () => ({
        validate: validateAllFields,
        scrollToFirstError,
        getErrorCount: () => Object.keys(errors).length,
      }),
      [validateAllFields, scrollToFirstError, errors]
    );

    const simOptions: SimOption[] = isMobileM2MProduct(onlineModel)
      ? [
          SimOption.ORDER_NEW_PHYSICAL_MINI_MICRO,
          SimOption.ORDER_NEW_PHYSICAL_NANO,
          SimOption.ACTIVATE_EXISTING_PHYSICAL,
        ]
      : [SimOption.ACTIVATE_EXISTING_PHYSICAL, SimOption.ORDER_NEW_ESIM, SimOption.ORDER_NEW_PHYSICAL];

    const isActivateExisting = formState.simType === ACTIVATE_EXISTING;
    const simCardConfiguration: SimCardConfiguration = {
      simSelection: isActivateExisting ? SimCardSelection.ACTIVATE_EXISTING : SimCardSelection.ORDER_NEW,
      simType: isActivateExisting ? SimType.PHYSICAL : (formState.simType as SimType),
      simCardNumber: formState.simCardNumber,
    };

    const phoneNumberOwner: PhoneNumberOwner | undefined =
      formState.phoneNumberType === PhoneNumberType.EXISTING
        ? {
            ownerType: formState.differentDonor ? PhoneNumberOwnerType.PERSON : PhoneNumberOwnerType.COMPANY,
            personEmail: formState.donorEmail,
            permitDonoringOnFixedContract: formState.fixedTermContract,
          }
        : undefined;

    return (
      <FormProvider {...innerFormMethods}>
        <div className={classNames(dsClass.PADDING_TOP_6)} id={`subscription-config-${cartItemInstanceId}`}>
          {showSelectPhoneNumber && (
            <SelectPhoneNumber
              cartItemInstanceId={cartItemInstanceId}
              companyName={companyName}
              datePickerLabel={t.MYX9('Pick a date')}
              datePickerLabelOptionalText=""
              disabledPhoneNumbers={disabledPhoneNumbers}
              existingPhoneNumberLabel={t.MYX7('Fill in existing phonenumber')}
              getNextAllowedDay={getNextAllowedDay}
              holidays={holidays}
              initiallySelectedPhoneNumber={formState.newPhoneNumber || initialPhoneNumber}
              isAllowedTransferDay={isAllowedTransferDay}
              onSelectUseExistingPhoneNumber={() => {}} // Not used in place order
              personOwnerEmailLabel={t.MYX8('Fill in email address')}
              phoneNumbers={phoneNumbers}
              phoneNumbersLoading={phoneNumbersLoading}
              phoneNumberOwner={phoneNumberOwner}
              selectedPhoneNumber={
                formState.phoneNumberType === PhoneNumberType.EXISTING
                  ? {
                      type: PhoneNumberType.EXISTING,
                      existingPhoneNumber: formState.existingPhoneNumber,
                      transferDate: (() => {
                        try {
                          return formState.transferDate
                            ? getNormalizedDateFromDateString(formState.transferDate)
                            : getNextAllowedDay(holidays);
                        } catch {
                          return getNextAllowedDay(holidays);
                        }
                      })(),
                    }
                  : {
                      type: PhoneNumberType.NEW,
                      newPhoneNumber: formState.newPhoneNumber,
                    }
              }
              validatedPhoneNumbers={validatedPhoneNumbers}
              onLoadMore={onLoadMoreNumbers}
            />
          )}

          {showNumberPrivacy && <NumberPrivacy cartItemInstanceId={cartItemInstanceId} />}

          {(showSimCard || showM2MSimCard) && (
            <PlaceOrderSimCard
              cartItemInstanceId={cartItemInstanceId}
              onClickSimCardNumberHelp={() => openDialog({ type: DialogType.ORDER_SIM_CARD_NUMBER_HELP })}
              simCardConfiguration={simCardConfiguration}
              simOptions={simOptions}
              simCardNumbers={usedSimCardNumbers}
              backendError={errors.simCardNumber}
            />
          )}
        </div>
      </FormProvider>
    );
  }
);

SubscriptionConfigurationHandler.displayName = 'SubscriptionConfigurationHandler';
